import { useCallback, useEffect, useState } from "react";

interface PaymentState {
  isProcessing: boolean;
  error: string | null;
  success: boolean;
}

interface PaymentMessage {
  action: string;
  status: "success" | "failed";
  message: string;
  jkcode: string;
  data: any;
}

interface UsePaymentHandlerProps {
  onSuccess?: (message: PaymentMessage) => void;
  onError?: (message: PaymentMessage) => void;
  targetOrigin?: string;
  iframeId?: string;
}

export const usePaymentHandler = ({
  onSuccess,
  onError,
  targetOrigin = "https://junk-king.com/",
  iframeId = "customer-payment-iframe",
}: UsePaymentHandlerProps = {}) => {
  const [paymentState, setPaymentState] = useState<PaymentState>({
    isProcessing: false,
    error: null,
    success: false,
  });

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      const message: PaymentMessage = event.data;
      if (message.action === "response") {
        setPaymentState((prev) => ({ ...prev, isProcessing: false }));
        if (message.status === "success") {
          setPaymentState((prev) => ({ ...prev, success: true, error: null }));
          onSuccess?.(message);
        } else if (message.status === "failed") {
          setPaymentState((prev) => ({
            ...prev,
            error: message.message,
            success: false,
          }));
          onError?.(message);
        }
      }
    },
    [onSuccess, onError, targetOrigin]
  );

  useEffect(() => {
    // Ensure the iframe is ready to receive messages
    const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
    if (!iframe) {
      return;
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  const initiatePayment = useCallback(
    (
      jkcode: string,
      targetUrl: string = "https://junkware-stage.junk-king.com/"
    ) => {
      const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
      if (!iframe?.contentWindow) {
        setPaymentState((prev) => ({
          ...prev,
          error: "Payment iframe not found or not loaded",
          isProcessing: false,
        }));
        return false;
      }

      setPaymentState((prev) => ({
        ...prev,
        isProcessing: true,
        error: null,
        success: false,
      }));

      iframe.contentWindow.postMessage({ jkcode }, targetUrl);
      return true;
    },
    [iframeId]
  );

  const resetPaymentState = useCallback(() => {
    setPaymentState({
      isProcessing: false,
      error: null,
      success: false,
    });
  }, []);

  return {
    paymentState,
    initiatePayment,
    resetPaymentState,
  };
};
